import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

export interface CollectionFormData {
  name: string;
  productIds: string[];
}

interface CollectionFormModalProps {
  isOpen: boolean;
  isEdit: boolean;
  initialData?: Partial<CollectionFormData>;
  products: { id: string; name: string }[];
  loading: boolean;
  onClose: () => void;
  onSubmit: (data: CollectionFormData) => void;
}

export default function CollectionFormModal({
  isOpen,
  isEdit,
  initialData,
  products,
  loading,
  onClose,
  onSubmit,
}: CollectionFormModalProps) {
  const { t } = useTranslation("dashboard");
  const [form, setForm] = useState<CollectionFormData>({
    name: initialData?.name || "",
    productIds: initialData?.productIds || [],
  });

  const toggleProduct = useCallback((id: string) => {
    setForm((prev) => ({
      ...prev,
      productIds: prev.productIds.includes(id)
        ? prev.productIds.filter((pid) => pid !== id)
        : [...prev.productIds, id],
    }));
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSubmit(form);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/60">
          <h3 className="font-heading text-base text-foreground-950">
            {isEdit ? t("catalog.editCollection") : t("catalog.createCollection")}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1">
              {t("catalog.collectionName")} *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder={t("catalog.collectionNamePlaceholder")}
              className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-2">
              {t("catalog.selectProducts")} ({form.productIds.length})
            </label>
            <div className="max-h-60 overflow-y-auto space-y-1.5 rounded-xl border border-background-200/60 p-3 bg-background-50">
              {products.length === 0 ? (
                <p className="text-sm text-foreground-400 text-center py-4">
                  {t("catalog.noProductsForCollection")}
                </p>
              ) : (
                products.map((p) => (
                  <label
                    key={p.id}
                    className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-background-100 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={form.productIds.includes(p.id)}
                      onChange={() => toggleProduct(p.id)}
                      className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
                    />
                    <span className="text-sm text-foreground-800">{p.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-full text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("catalog.cancel")}
            </button>
            <button
              type="submit"
              disabled={loading || !form.name.trim()}
              className="px-5 py-2 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              {loading && <i className="ri-loader-4-line animate-spin text-sm"></i>}
              {isEdit ? t("catalog.saveChanges") : t("catalog.createCollectionBtn")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}